import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ErrorDisplay, LoadingState } from '../components/common';
import { HourDateTimeSelect, ResourceSelect, UserSelect } from '../components/forms';
import WaitlistButton from '../components/waitlist/WaitlistButton';
import { reservationsApi, resourcesApi, usersApi } from '../services/api';
import { ReservationRequest, Resource, User } from '../types';

// Helper function to create a date in Mountain Time
const createMountainTimeDate = (year: number, month: number, day: number, hour: number, minute: number = 0): Date => {
  // Create date in local timezone
  const date = new Date(year, month - 1, day, hour, minute, 0);

  // Convert to Mountain Time (UTC-6 or UTC-7 depending on daylight saving)
  // For simplicity, we'll use a fixed offset of UTC-6 (Mountain Time during standard time)
  // In a production app, you would use a proper timezone library like moment-timezone or date-fns-tz
  const mountainTimeOffset = -6 * 60; // -6 hours in minutes

  // Get the user's local timezone offset in minutes
  const localOffset = date.getTimezoneOffset();

  // Calculate the difference between local and Mountain Time
  const offsetDiff = localOffset - mountainTimeOffset;

  // Adjust the date by the offset difference
  date.setMinutes(date.getMinutes() + offsetDiff);

  return date;
};

const CreateReservation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const resourceIdFromUrl = params.id ? parseInt(params.id, 10) : null;

  // Get the current user from auth context
  const { user, isAuthenticated } = useAuth();

  const [resources, setResources] = useState<Resource[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasOverlap, setHasOverlap] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [selectedResources, setSelectedResources] = useState<Resource[]>([]);
  const [multiMode, setMultiMode] = useState(false);

  // Get date from URL query parameter if available
  const queryParams = new URLSearchParams(location.search);
  const dateParam = queryParams.get('date');

  // Debug parameters
  console.log('Date parameter from URL:', dateParam);
  console.log('Resource ID from URL:', resourceIdFromUrl);

  // Form state
  const [resourceId, setResourceId] = useState<number | ''>(resourceIdFromUrl || '');
  const [userId, setUserId] = useState<number | ''>(user?.id || '');
  const [startDate, setStartDate] = useState<Date | null>(() => {
    // If date param exists, set start date to 9am on the selected date in Mountain Time
    if (dateParam) {
      // Parse the date carefully to use Mountain Time
      const [year, month, day] = dateParam.split('-').map(num => parseInt(num, 10));
      // Explicitly create a date with 9 AM
      const date = new Date(year, month - 1, day);
      date.setHours(9, 0, 0, 0); // 9:00 AM
      console.log('Created start date:', date);
      return date;
    }
    return new Date(); // Current local time if no date param
  });

  const [endDate, setEndDate] = useState<Date | null>(() => {
    // If date param exists, set end date to 6pm on the same date
    if (dateParam) {
      // Parse the date carefully
      const [year, month, day] = dateParam.split('-').map(num => parseInt(num, 10));
      // Explicitly create a date with 6 PM
      const date = new Date(year, month - 1, day);
      date.setHours(18, 0, 0, 0); // 6:00 PM
      console.log('Created end date:', date);
      return date;
    }
    // Default to 2 hours after current time if no date param
    return new Date(Date.now() + 2 * 60 * 60 * 1000);
  });
  const [notes, setNotes] = useState<string>('');

  // Form validation
  const [errors, setErrors] = useState<{
    resourceId?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
  }>({});

  // Set the user ID when the current user changes
  useEffect(() => {
    if (user) {
      setUserId(user.id);
    }
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [resourcesData, usersData] = await Promise.all([
          resourcesApi.getAll(),
          usersApi.getAll()
        ]);

        const activeResources = resourcesData.filter(r => r.isActive);
        setResources(activeResources);
        setUsers(usersData);

        // If resourceId is set from URL, find the resource object
        if (resourceId) {
          const resource = activeResources.find(r => r.id === resourceId);
          if (resource) {
            setSelectedResource(resource);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load resources and users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [resourceId]);

  const validateForm = (): boolean => {
    const newErrors: { resourceId?: string; userId?: string; startDate?: string; endDate?: string; } = {};

    if (multiMode) {
      if (selectedResources.length === 0) {
        newErrors.resourceId = 'Select at least one resource';
      }
    } else if (!resourceId) {
      newErrors.resourceId = 'Please select a resource';
    }

    if (!userId) {
      newErrors.userId = 'Please select a user';
    }

    if (!startDate) {
      newErrors.startDate = 'Please select a start date and time';
    }

    if (!endDate) {
      newErrors.endDate = 'Please select an end date and time';
    } else if (startDate && endDate && endDate <= startDate) {
      newErrors.endDate = 'End time must be after start time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      if (multiMode) {
        // Overlap check for any selected resource
        for (const res of selectedResources) {
          const overlapping = await reservationsApi.checkOverlapping(
            res.id,
            startDate!.toISOString(),
            endDate!.toISOString()
          );
          if (overlapping) {
            setHasOverlap(true);
            setErrors(prev => ({ ...prev, resourceId: `Resource '${res.name}' is already reserved in that period` }));
            setSubmitting(false);
            return;
          }
        }
        setHasOverlap(false);
        const batchPayload = {
          resourceIds: selectedResources.map(r => r.id),
          userId: userId as number,
          startDate: startDate!.toISOString(),
          endDate: endDate!.toISOString(),
          notes: notes // keep user-provided notes only; no default batch label
        };
        await reservationsApi.createBatch(batchPayload);
        navigate('/reservations');
      } else {
        // Single mode existing logic
        const overlapping = await reservationsApi.checkOverlapping(
          resourceId as number,
          startDate!.toISOString(),
          endDate!.toISOString()
        );
        if (overlapping) {
          setHasOverlap(true);
          setErrors({ ...errors, resourceId: 'This resource is already reserved during the selected time period' });
          setSubmitting(false);
          return;
        }
        setHasOverlap(false);
        const reservationRequest: ReservationRequest = {
          resourceId: resourceId as number,
          userId: userId as number,
          startDate: startDate!.toISOString(),
          endDate: endDate!.toISOString(),
          notes: notes || 'New Reservation'
        };
        await reservationsApi.create(reservationRequest);
        navigate('/reservations');
      }
    } catch (err: any) {
      console.error('Error creating reservation(s):', err);
      
      // Check if the error message contains the ski pass booking restriction
      const errorMessage = err?.response?.data?.error || err?.message || '';
      if (errorMessage.includes('ski pass') && errorMessage.includes('30 days')) {
        setError('Ski Pass reservations can only be made up to 30 days in advance. Please select a date within the next 30 days.');
      } else if (errorMessage) {
        setError(`Failed to create reservation: ${errorMessage}`);
      } else {
        setError('Failed to create reservation(s). Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading resources..." />;
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={() => window.location.reload()} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Create Reservation</Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/reservations')}
        >
          Back to Reservations
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={<Switch checked={multiMode} onChange={(e) => {
                setMultiMode(e.target.checked);
                setErrors({});
                setHasOverlap(false);
                // no-op: removed batchResultIds reset
              }} />}
              label="Multi-resource mode"
            />
          </Box>
          {multiMode ? (
            <Box sx={{ mb: 3 }}>
              <Autocomplete
                multiple
                options={resources}
                getOptionLabel={(option) => option.name}
                value={selectedResources}
                onChange={(_, value) => {
                  setSelectedResources(value);
                  setHasOverlap(false);
                  setErrors(prev => ({ ...prev, resourceId: undefined }));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.name} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Resources"
                    error={!!errors.resourceId}
                    helperText={errors.resourceId}
                  />
                )}
              />
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <ResourceSelect
                resources={resources}
                value={resourceId}
                onChange={(value) => {
                  setResourceId(value);
                  setSelectedResource(resources.find(r => r.id === value) || null);
                  setHasOverlap(false);
                }}
                error={errors.resourceId}
              />
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <UserSelect
              users={users}
              value={userId}
              onChange={setUserId}
              error={errors.userId}
              disabled={user?.role !== 'admin'}
              helperText={user?.role !== 'admin' ? 'Non-admin users can only make reservations for themselves' : undefined}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <HourDateTimeSelect
              label="Start Date & Hour"
              value={startDate}
              onChange={(d) => {
                setStartDate(d);
                if (d && endDate && endDate <= d) {
                  const newEnd = new Date(d);
                  newEnd.setHours(d.getHours() + 1);
                  setEndDate(newEnd);
                }
              }}
              error={errors.startDate}
              minDate={new Date()}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <HourDateTimeSelect
              label="End Date & Hour"
              value={endDate}
              onChange={(d) => setEndDate(d)}
              error={errors.endDate}
              minDate={startDate || new Date()}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              placeholder="Add any additional notes or requirements"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}>
            {hasOverlap && selectedResource && (
              <WaitlistButton
                resourceId={selectedResource.id}
                resourceName={selectedResource.name}
                startDate={startDate!}
                endDate={endDate!}
                onSuccess={() => navigate('/my-waitlist')}
              />
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : undefined}
            >
              {submitting ? (multiMode ? 'Creating Batch...' : 'Creating...') : multiMode ? 'Create Reservations' : 'Create Reservation'}
            </Button>
          </Box>

          {/* Success alert removed: redirect occurs immediately after successful batch creation */}
        </form>
      </Paper>
    </Box>
  );
};

export default CreateReservation;
